import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';

import { env } from '@amfam/environment';
import { DataDogService } from '@amfam/shared/analytics/lib/services/data-dog.service';
import {
  CONNECT_PARTNER_IDS,
  PARTNER_ID_BY_EXPERIENCE_ID
} from '@amfam/shared/utility/brand/lib/services/partner-mappings';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access/lib/services/feature-flag.service';
import { fromRouterActions } from '@amfam/shared/utility/navigation/lib/+state/router-actions';
import {
  ROUTE_PARAMS,
  SESSION_STORAGE_KEYS
} from '@amfam/shared/utility/shared-services/lib/storage/storage-keys';

import { ForgerockAuthService } from '../core/auth/forgerock-auth.service';
import { selectLoggedIn } from '../core/store/index';

function getPartnerId(context: string): string | undefined {
  for (const [key, value] of Object.entries(PARTNER_ID_BY_EXPERIENCE_ID)) {
    if (Array.isArray(value)) {
      if (value.includes(context)) {
        return key;
      }
    }
  }
}

export const loginRedirectGuard: CanActivateFn = async (route, state) => {
  const forgerockAuthService = inject(ForgerockAuthService);
  const featureFlagService = inject(FeatureFlagService);
  const store = inject(Store);
  const dataDogService = inject(DataDogService);

  const url = state.url;
  const expid = route.paramMap.get(ROUTE_PARAMS.EXPERIENCE_ID);
  if (expid && featureFlagService.isEnabled('connect_redirect_to_afi')) {
    const partnerId = getPartnerId(expid);

    if (partnerId && CONNECT_PARTNER_IDS.includes(partnerId)) {
      if (url.includes('/forgot-userid') || url.includes('/session-expired')) {
        window.location.href = url.replace(`/${expid}`, '');
        return true;
      } else if (url.includes('/forgot-password')) {
        forgerockAuthService.getTokens(env.forgerock.login, false).catch(error => {
          dataDogService.log(
            'ForgeRock session expired or invalid during forgot password',
            'warn',
            {
              error: error,
              url: url
            }
          );
          if (env.environment !== 'prod') {
            console.warn('ForgeRock session expired or invalid:', error);
          }
        });
        return false;
      } else {
        window.location.href = window.location.origin;
        return false;
      }
    } else if (url.includes('/forgot-userid') || url.includes('/session-expired')) {
      return true;
    }
  }

  const loggedIn = await firstValueFrom(store.select(selectLoggedIn));

  if (loggedIn) {
    store.dispatch(
      fromRouterActions.Go({
        path: ['/overview']
      })
    );
    return false;
  }

  //partners
  if (expid) {
    sessionStorage.setItem(SESSION_STORAGE_KEYS.EXPERIENCE_ID, expid);

    forgerockAuthService.getTokens(env.forgerock.login, false).catch(error => {
      dataDogService.log('ForgeRock session expired or invalid for partner', 'warn', {
        error: error,
        expid: expid
      });
      if (env.environment !== 'prod') {
        console.warn('ForgeRock session expired or invalid:', error);
      }
    });
    return false;
  }

  return true;
};
